import { HxAccount, HxCollection, SessionId } from '@outlook/hxcore_web';
import { getHxInstance } from './getHxInstance';
import { runInHxSession } from './runInHxSession';
import { waitForHxItemCreation } from './waitForHxItemCreation';
import { waitForInitialFolderSync } from './waitForInitialFolderSync';
import { addNewAccount } from 'hx-account';
import { getSelectedAccount, ACCOUNT_CREATION_TIMEOUT } from './accountManager';
import { handleHxObjectLoadResult } from 'hx-object-load-helpers';
import { isBootFeatureEnabled } from 'owa-metatags';
import { lazySetSelectedAccount } from 'owa-mail-account-store';
import { setProfileStoreAccounts } from './setProfileStoreAccounts';

let hxAccount;
let hxAccountPromise: Promise<HxAccount> | undefined;

// Once owa supports multi-account we expect that resolvers will always
// be told (explicitly or implicitly) what accounts to use.
// But for now we assume there is a single account.
export async function getHxAccount(): Promise<HxAccount> {
    if (isBootFeatureEnabled('monaccounts')) {
        return getSelectedAccount();
    }

    if (!hxAccount) {
        const hxAccounts = handleHxObjectLoadResult(
            await runInHxSession(async (sessionId: SessionId) => {
                const root = await getHxInstance().activeSet.getRoot(sessionId);
                return root.getAccountsSyncingMailOrCalendar(sessionId);
            })
        );

        setProfileStoreAccounts(hxAccounts);

        if (hxAccounts.items.length > 0) {
            hxAccount = hxAccounts.items[0];
            waitForInitialFolderSync(hxAccount);
        } else {
            // looks like we are in initial bootstrapping so create the hx account.
            if (!hxAccountPromise) {
                hxAccountPromise = addHxAccount(hxAccounts);
            }

            hxAccount = await hxAccountPromise;
        }
    }

    if (hxAccount) {
        lazySetSelectedAccount.importAndExecute(hxAccount.stableAccountId);
    }

    return hxAccount;
}

async function addHxAccount(hxAccounts: HxCollection<HxAccount>): Promise<HxAccount> {
    addNewAccount();
    const hxAccount = await waitForHxItemCreation(hxAccounts, ACCOUNT_CREATION_TIMEOUT);
    await waitForInitialFolderSync(hxAccount);
    return hxAccount;
}
